/**
 * Страница «Возвраты».
 *
 * Показывает все возвраты, включая легаси — заказы, у которых статус
 * «Возврат» был проставлен руками до появления этой сущности. Такие строки
 * перенесены миграцией 0031 без суммы и причины и помечены как незаполненные:
 * их надо дозаполнить кнопкой в строке.
 *
 * Сами возвраты оформляются из карточки заказа («Сделать возврат»).
 */
import React, { useEffect, useMemo, useState } from "react";
import client from "../../api/client";
import { Layout } from "../../components/Layout/Layout";
import User from "../../components/User";
import RefundModal from "../../components/RefundModal";
import { useInHouse } from "../../context/inHousePermissions";
import { formatRelative } from "../../utils/formatRelative";
import { canMakeRefund } from "../../utils/roleAccess";
import { RefundRow, fmtAmount, reasonLabel } from "../../utils/refunds";
// Переиспользуем стили страницы продаж (.tbl, .btn, .sales-head).
import "../sales/index.css";
import "./index.css";

const Refunds: React.FC = () => {
  const { currentUserRecord } = useInHouse();
  const mayEdit = canMakeRefund(currentUserRecord?.role);

  const [rows, setRows] = useState<RefundRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  // Дозаполнение легаси-строки.
  const [editing, setEditing] = useState<RefundRow | null>(null);
  // Попап заказа по клику на юзернейм.
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [rowLoadingId, setRowLoadingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const fetchRows = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await client.get<RefundRow[]>("/refunds");
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError("Не удалось загрузить возвраты");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.username || "").toLowerCase().includes(q));
  }, [rows, query]);

  const needsInfoCount = useMemo(
    () => rows.filter((r) => r.needsInfo).length,
    [rows],
  );

  const openUser = async (row: RefundRow) => {
    if (rowLoadingId || !row.username) return;
    setRowLoadingId(row.id);
    try {
      const { data } = await client.get(`/orders/${row.username}`);
      setSelectedUser({ username: row.username, ...data });
    } catch (err) {
      console.error("Failed to load order for", row.username, err);
    } finally {
      setRowLoadingId(null);
    }
  };

  const remove = async (row: RefundRow) => {
    const ok = window.confirm(
      row.isFull
        ? `Удалить возврат ${row.username || ""}? Заказу вернётся статус, который был до возврата.`
        : `Удалить возврат ${row.username || ""}?`,
    );
    if (!ok) return;
    setDeletingId(row.id);
    try {
      await client.delete(`/refunds/${row.id}`);
      setRows((arr) => arr.filter((r) => r.id !== row.id));
    } catch (err: any) {
      alert(err?.response?.data?.error || "Не удалось удалить возврат");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Layout>
      <div className="sales-head">
        <h2 style={{ fontSize: 18, fontWeight: 600 }}>Возвраты</h2>
        <input
          className="rfp-search"
          type="text"
          placeholder="Поиск по юзернейму…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {needsInfoCount > 0 && (
        <div className="rfp-notice">
          Возвратов без суммы и причины: {needsInfoCount}. Это возвраты,
          оформленные до появления этой страницы — дозаполните их.
        </div>
      )}

      {error && <div className="rfp-error">{error}</div>}

      {loading ? (
        <div className="rfp-empty">Загрузка…</div>
      ) : visible.length === 0 ? (
        <div className="rfp-empty">Возвратов нет</div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>Юзернейм</th>
              <th>Сумма возврата</th>
              <th>Дата покупки</th>
              <th>Дата возврата</th>
              <th>Причина</th>
              <th>Статус</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.id} className={r.needsInfo ? "rfp-row--todo" : undefined}>
                <td>
                  <button
                    type="button"
                    className="rfp-link"
                    disabled={!r.username || rowLoadingId === r.id}
                    onClick={() => openUser(r)}
                  >
                    {r.username || "—"}
                  </button>
                </td>
                <td className="rfp-amount">
                  {r.amount == null ? (
                    <span className="rfp-todo">не указана</span>
                  ) : (
                    <>
                      {fmtAmount(r.amount)} / {fmtAmount(r.paidTotal)} {r.currency}
                      {r.isFull && <span className="rfp-badge">полный</span>}
                    </>
                  )}
                </td>
                <td>{r.purchaseDate ? formatRelative(r.purchaseDate) : "—"}</td>
                <td>{r.refundedAt ? formatRelative(r.refundedAt) : "—"}</td>
                <td>
                  {r.reason ? (
                    reasonLabel(r.reason, r.reasonText)
                  ) : (
                    <span className="rfp-todo">не указана</span>
                  )}
                </td>
                <td className="rfp-status">{r.orderStatus || "—"}</td>
                <td className="rfp-actions">
                  {mayEdit && (
                    <>
                      {r.needsInfo && (
                        <button
                          type="button"
                          className="rfp-btn"
                          onClick={() => setEditing(r)}
                        >
                          Дозаполнить
                        </button>
                      )}
                      <button
                        type="button"
                        className="rfp-btn rfp-btn--danger"
                        disabled={deletingId === r.id}
                        onClick={() => remove(r)}
                      >
                        {deletingId === r.id ? "Удаление…" : "Удалить"}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <RefundModal
          userId={editing.userId}
          username={editing.username || ""}
          refundId={editing.id}
          initial={{
            amount: editing.amount,
            currency: editing.currency,
            reason: editing.reason,
            reasonText: editing.reasonText,
          }}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            fetchRows();
          }}
        />
      )}

      {selectedUser && (
        <User
          user={selectedUser}
          deadline=""
          onClose={() => setSelectedUser(null)}
          onUpdate={() => {
            setSelectedUser(null);
            fetchRows();
          }}
        />
      )}
    </Layout>
  );
};

export default Refunds;
